import type { QueryClient } from '@tanstack/react-query';
import type { GetMeResponsePayload } from '#services/api/user.ts';

import { redirect } from 'react-router';

import { identifyPendoVisitor } from '#lib/pendo.ts';
import {
  me as meQueryOptions,
  properties as propertiesQueryOptions,
} from '#lib/react-query/queries.ts';
import { getAccessToken } from './cookies';
import { isTokenValid } from './jwt.ts';
import { isUserAllowedInsights } from './roles.ts';

/**
 * Require the user to be authenticated before accessing a route.
 *
 * It checks if the user has a valid token and then fetches the user data and
 * updates the query client's data. If the token is invalid or the user data
 * cannot be fetched, it redirects to the login page.
 */
async function requireAuthenticatedUser(queryClient: QueryClient): Promise<GetMeResponsePayload> {
  if (!isTokenValid(getAccessToken())) {
    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect('/login');
  }

  /**
   * The token _should_ technically be valid by this point unless directly
   * tampered with or there's a problem in the API.
   */
  const me = await queryClient.ensureQueryData(meQueryOptions).catch(() => undefined);

  if (!me) {
    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect('/login');
  }

  /**
   * Identified here rather than after the Signal entitlement check, so a user bounced to
   * `/unauthorized` is still measured — Pendo sends nothing at all until `initialize` runs.
   */
  identifyPendoVisitor(me);

  return me;
}

/**
 * Require the user to be a guest before accessing a route.
 *
 * No need to try to fetch the user data since we're assuming the token is
 * valid, if this turns out not to be the case after the redirect, the next
 * route's loader will take care of handling the error.
 */
export function requireGuestUser() {
  if (isTokenValid(getAccessToken())) {
    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect('/');
  }
}

/**
 * Require the user to be authenticated and authorized to access insights.
 *
 * This function combines authentication and authorization checks:
 * 1. Verifies the user is authenticated
 * 2. Fetches user and properties data
 * 3. Checks if the user's role allows access to insights
 * 4. Returns user data if authorized, otherwise throws redirect to unauthorized page
 */
export async function requireAuthorizedInsightsUser(
  queryClient: QueryClient
): Promise<GetMeResponsePayload> {
  // First ensure user is authenticated
  const user = await requireAuthenticatedUser(queryClient);

  // Fetch properties to check authorization
  const properties = await queryClient.ensureQueryData(propertiesQueryOptions).catch(() => {
    throw new Error('Failed to fetch settings');
  });

  // Check if user is authorized for insights
  if (!isUserAllowedInsights(user.user.role, properties)) {
    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect('/unauthorized');
  }

  return user;
}
